/**
 * Task Map Component
 * Shows task locations on a Mapbox map
 */

import { useEffect, useRef, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import { cn } from '../../utils/cn';
import { Layers } from 'lucide-react';
import type { RoutingTask, Zone } from '../../types';
import { getZones } from '../../services/zones.api';
import { setMapLanguageToArabic, ARABIC_MAP_STYLE } from '../../utils/mapbox';
import { isValidCoordinate } from '../../utils/coordinates';
import { BAGHDAD_WAREHOUSE } from './warehouse';

interface TaskMapProps {
  tasks: RoutingTask[];
  selectedTasks: Set<string>;
  onTaskSelect?: (taskUid: string) => void;
  className?: string;
  showWarehouse?: boolean;
}

// Default center (Baghdad Warehouse)
const DEFAULT_CENTER: [number, number] = [BAGHDAD_WAREHOUSE.lng, BAGHDAD_WAREHOUSE.lat];
const DEFAULT_ZOOM = 11;

export default function TaskMap({
  tasks,
  selectedTasks,
  onTaskSelect,
  className,
  showWarehouse = true,
}: TaskMapProps) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);
  const markers = useRef<mapboxgl.Marker[]>([]);
  const warehouseMarker = useRef<mapboxgl.Marker | null>(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [showZones, setShowZones] = useState(false);
  const [zones, setZones] = useState<Zone[]>([]);
  const [zonesLoaded, setZonesLoaded] = useState(false);
  const cityId = 1; // Baghdad

  // Initialize map
  useEffect(() => {
    if (!mapContainer.current || map.current) return;

    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: ARABIC_MAP_STYLE,
      center: DEFAULT_CENTER,
      zoom: DEFAULT_ZOOM,
    });

    map.current.addControl(new mapboxgl.NavigationControl(), 'top-right');

    // Set Arabic language for map labels
    setMapLanguageToArabic(map.current);

    map.current.on('load', () => {
      setMapLoaded(true);
      // Trigger resize after load to ensure tiles fill container
      setTimeout(() => map.current?.resize(), 100);
      setTimeout(() => map.current?.resize(), 500);
    });

    return () => {
      map.current?.remove();
      map.current = null;
    };
  }, []);

  // Resize map when container size changes
  useEffect(() => {
    if (!wrapperRef.current || !map.current) return;

    const resizeObserver = new ResizeObserver(() => {
      // Small delay to ensure container has finished resizing
      setTimeout(() => map.current?.resize(), 0);
    });

    resizeObserver.observe(wrapperRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, [mapLoaded]);

  // Fetch zones when component mounts
  useEffect(() => {
    async function fetchZones() {
      try {
        const zonesData = await getZones(cityId);
        setZones(zonesData as Zone[]);
        setZonesLoaded(true);
      } catch (error) {
        console.error('Failed to fetch zones:', error);
      }
    }
    fetchZones();
  }, [cityId]);

  // Add/remove zone layers when showZones changes
  useEffect(() => {
    if (!map.current || !mapLoaded || !zonesLoaded) return;

    const mapInstance = map.current;

    if (showZones) {
      // Add zones as polygon layers
      zones.forEach((zone) => {
        const sourceId = `zone-source-${zone._id}`;
        const fillLayerId = `zone-fill-${zone._id}`;
        const outlineLayerId = `zone-outline-${zone._id}`;

        // Skip if source already exists
        if (mapInstance.getSource(sourceId)) return;

        // Add source
        mapInstance.addSource(sourceId, {
          type: 'geojson',
          data: {
            type: 'Feature',
            properties: {
              name: zone.name,
              color: zone.color,
            },
            geometry: zone.polygon,
          },
        });

        // Add fill layer (below markers)
        mapInstance.addLayer({
          id: fillLayerId,
          type: 'fill',
          source: sourceId,
          paint: {
            'fill-color': zone.color,
            'fill-opacity': 0.3,
          },
        }, 'aeroway-polygon'); // Insert below most layers

        // Add outline layer
        mapInstance.addLayer({
          id: outlineLayerId,
          type: 'line',
          source: sourceId,
          paint: {
            'line-color': zone.color,
            'line-width': 2,
            'line-opacity': 0.8,
          },
        });

      });
    } else {
      // Remove zone layers
      zones.forEach((zone) => {
        const sourceId = `zone-source-${zone._id}`;
        const fillLayerId = `zone-fill-${zone._id}`;
        const outlineLayerId = `zone-outline-${zone._id}`;

        if (mapInstance.getLayer(outlineLayerId)) {
          mapInstance.removeLayer(outlineLayerId);
        }
        if (mapInstance.getLayer(fillLayerId)) {
          mapInstance.removeLayer(fillLayerId);
        }
        if (mapInstance.getSource(sourceId)) {
          mapInstance.removeSource(sourceId);
        }
      });
    }
  }, [showZones, zones, mapLoaded, zonesLoaded]);

  // Add warehouse marker
  useEffect(() => {
    if (!map.current || !mapLoaded || !showWarehouse) return;

    // Remove existing warehouse marker if any
    if (warehouseMarker.current) {
      warehouseMarker.current.remove();
    }

    // Create warehouse marker element
    const el = document.createElement('div');
    el.className = 'warehouse-marker';
    el.style.cssText = `
      width: 36px;
      height: 36px;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
    `;
    
    // Warehouse icon (building/warehouse SVG)
    el.innerHTML = `
      <svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="18" cy="18" r="16" fill="#7C3AED" stroke="white" stroke-width="3"/>
        <path d="M11 24V15L18 10L25 15V24H20V19H16V24H11Z" fill="white"/>
      </svg>
    `;

    // Create popup for warehouse
    const popup = new mapboxgl.Popup({
      offset: 25,
      closeButton: false,
    }).setHTML(`
      <div style="padding: 8px;">
        <div style="font-weight: 600; margin-bottom: 4px;">${BAGHDAD_WAREHOUSE.name}</div>
        <div style="font-size: 12px; color: #666;">
          <div>Sorting Center / Warehouse</div>
          <div>Start & End point for all routes</div>
        </div>
      </div>
    `);

    warehouseMarker.current = new mapboxgl.Marker({
      element: el,
      anchor: 'center',
    })
      .setLngLat([BAGHDAD_WAREHOUSE.lng, BAGHDAD_WAREHOUSE.lat])
      .setPopup(popup)
      .addTo(map.current);

    return () => {
      warehouseMarker.current?.remove();
      warehouseMarker.current = null;
    };
  }, [mapLoaded, showWarehouse]);

  // Update markers when tasks change
  useEffect(() => {
    if (!map.current || !mapLoaded) return;

    // Clear existing markers
    markers.current.forEach((marker) => marker.remove());
    markers.current = [];

    if (tasks.length === 0) return;

    // Create markers for each task, filtering invalid coordinates
    tasks.forEach((task) => {
      if (!isValidCoordinate(task.lat, task.lng)) {
        console.warn(`Skipping task with invalid coordinates: ${task.uid}`, { lat: task.lat, lng: task.lng });
        return;
      }

      const isSelected = selectedTasks.has(task.uid);
      const isPlanned = task.is_planned;
      const size = isSelected ? 24 : 20;
      
      // Create marker element with inner dot for hover effect
      const el = document.createElement('div');
      el.className = 'task-marker-wrapper';
      el.style.cssText = `
        width: ${size}px;
        height: ${size}px;
        cursor: ${isPlanned ? 'not-allowed' : 'pointer'};
        opacity: ${isPlanned ? '0.6' : '1'};
      `;

      // Determine marker color based on planned status
      let markerColor = task.type === 'PICK-UP-ORDERS' ? '#10B981' : '#F59E0B';
      let borderColor = isSelected ? '#3B82F6' : 'white';
      
      // Planned tasks get amber color with dashed border effect
      if (isPlanned) {
        markerColor = '#F59E0B'; // Amber for planned
        borderColor = '#D97706'; // Darker amber border
      }

      // Inner element for visual styling and hover effects
      const inner = document.createElement('div');
      inner.className = 'task-marker-inner';
      inner.style.cssText = `
        width: 100%;
        height: 100%;
        border-radius: 50%;
        background-color: ${markerColor};
        border: 2px solid ${borderColor};
        box-shadow: 0 2px 4px rgba(0,0,0,0.2);
        transition: transform 0.15s ease-out, box-shadow 0.15s ease-out;
      `;
      el.appendChild(inner);

      // Add hover effect on inner element only
      el.addEventListener('mouseenter', () => {
        inner.style.transform = 'scale(1.3)';
        inner.style.boxShadow = '0 4px 8px rgba(0,0,0,0.3)';
      });
      el.addEventListener('mouseleave', () => {
        inner.style.transform = 'scale(1)';
        inner.style.boxShadow = '0 2px 4px rgba(0,0,0,0.2)';
      });

      // Add click handler - only for non-planned tasks
      el.addEventListener('click', () => {
        if (!isPlanned) {
          onTaskSelect?.(task.uid);
        }
      });

      // Create popup with planned status info
      const plannedInfo = isPlanned 
        ? `<div style="color: #D97706; font-weight: 500; margin-top: 4px;">⚠️ Planned in: ${task.planned_route_name}</div>`
        : '';
      
      const popup = new mapboxgl.Popup({
        offset: 25,
        closeButton: false,
      }).setHTML(`
        <div style="padding: 8px;">
          <div style="font-weight: 600; margin-bottom: 4px;">${task.uid}</div>
          <div style="font-size: 12px; color: #666;">
            <div>${task.type} • ${task.number_of_orders} orders</div>
            <div>Status: ${task.task_status}</div>
            ${task.area_name ? `<div>Area: ${task.area_name}</div>` : ''}
            ${task.region_id ? `<div>Region: ${task.region_id}</div>` : ''}
            ${plannedInfo}
          </div>
        </div>
      `);

      // Create marker with anchor set to center
      const marker = new mapboxgl.Marker({
        element: el,
        anchor: 'center',
      })
        .setLngLat([task.lng, task.lat])
        .setPopup(popup)
        .addTo(map.current!);

      markers.current.push(marker);
    });

  }, [tasks, selectedTasks, mapLoaded, onTaskSelect]);

  // Fit bounds only when tasks change (not on selection change)
  useEffect(() => {
    if (!map.current || !mapLoaded || tasks.length === 0) return;

    const bounds = new mapboxgl.LngLatBounds();
    const validTasks = tasks.filter((task) => isValidCoordinate(task.lat, task.lng));
    if (validTasks.length === 0) return;
    validTasks.forEach((task) => {
      bounds.extend([task.lng, task.lat]);
    });
    map.current.fitBounds(bounds, {
      padding: 50,
      maxZoom: 14,
    });
  }, [tasks, mapLoaded]);

  // Show placeholder if no Mapbox token
  if (!mapboxgl.accessToken) {
    return (
      <div
        className={cn(
          'bg-gray-100 rounded-lg flex items-center justify-center',
          className
        )}
        style={{ minHeight: '400px' }}
      >
        <div className="text-center text-gray-500">
          <p className="font-medium">Map not available</p>
          <p className="text-sm">Set VITE_MAPBOX_TOKEN to enable map view</p>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={wrapperRef}
      className={cn('rounded-lg overflow-hidden relative', className)}
      style={{ minHeight: '400px', height: '100%', width: '100%' }}
    >
      {/* Map container - Mapbox takes over this div */}
      <div
        ref={mapContainer}
        style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
      />
      
      {/* Zones Toggle Button - positioned below the navigation controls */}
      <button
        onClick={() => setShowZones(!showZones)}
        className={cn(
          'absolute top-24 right-2.5 z-10 p-2 rounded shadow-md transition-colors',
          showZones 
            ? 'bg-purple-600 text-white hover:bg-purple-700' 
            : 'bg-white text-gray-700 hover:bg-gray-100'
        )}
        title={showZones ? 'Hide zones' : 'Show zones'}
      >
        <Layers className="h-4 w-4" />
      </button>
      
      {/* Zones Legend - show when zones are visible */}
      {showZones && zones.length > 0 && (
        <div className="absolute bottom-4 left-4 z-10 bg-white rounded-lg shadow-md p-3 max-w-xs">
          <div className="text-xs font-medium text-gray-700 mb-2">Zones</div>
          <div className="space-y-1">
            {zones.map((zone) => (
              <div key={zone._id} className="flex items-center gap-2 text-xs">
                <div 
                  className="w-3 h-3 rounded-sm border border-gray-300" 
                  style={{ backgroundColor: zone.color, opacity: 0.6 }}
                />
                <span className="text-gray-600">{zone.name}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
